import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Save,
  ArrowLeft,
  BookOpen,
  Code,
  FileText,
  Trash2,
  Edit3,
  CheckSquare,
  CheckCircle,
  Eye,
  Loader,
  AlertCircle,
  Clock,
  Users,
  Award,
  GripVertical,
  Copy,
  Settings,
  HelpCircle,
} from "lucide-react";
import { apiService } from "../services/api";
import "./CreateAssessment.css";

const CreateAssessment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editAssessmentId = searchParams.get("edit");
  const isEditMode = Boolean(editAssessmentId);

  const [assessmentData, setAssessmentData] = useState({
    title: "",
    description: "",
    skillId: "",
    type: "quiz",
    difficultyLevel: "beginner",
    maxScore: 0, // Will be auto-calculated from question points
    passingScore: 70,
    timeLimit: 30, // in minutes
    maxAttempts: 3,
    questions: [],
  });

  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedAssessmentId, setSubmittedAssessmentId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);

  const [currentQuestion, setCurrentQuestion] = useState({
    id: null,
    questionText: "",
    questionType: "multiple_choice",
    points: 10,
    options: [
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
    ],
  });

  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [draggedQuestion, setDraggedQuestion] = useState(null);

  // Fetch skills on component mount
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        setSkillsLoading(true);
        const response = await apiService.assessments.getSkills();
        if (response && !response.error) {
          setSkills(response);
        } else {
          throw new Error(response?.error || "Failed to fetch skills");
        }
      } catch (err) {
        console.error("Error fetching skills:", err);
        setError("Failed to load skills. Please refresh the page.");
      } finally {
        setSkillsLoading(false);
      }
    };

    fetchSkills();
  }, []);

  // Group skills by category (moved before useEffect to avoid initialization error)
  const skillsByCategory = React.useMemo(() => {
    return skills.reduce((acc, skill) => {
      const category = skill.category || "Other";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(skill);
      return acc;
    }, {});
  }, [skills]);

  // Debug: Log skills and categories
  useEffect(() => {
    if (skills.length > 0) {
      console.log("📊 Skills loaded:", skills.length);
      console.log("📁 Categories:", Object.keys(skillsByCategory));
      console.log("Skills by category:", skillsByCategory);
    }
  }, [skills, skillsByCategory]);

  // Load assessment data when in edit mode
  useEffect(() => {
    const loadAssessmentForEdit = async () => {
      if (!isEditMode || !editAssessmentId) return;

      try {
        setLoadingAssessment(true);
        setError(null);

        const response =
          await apiService.assessments.getAssessment(editAssessmentId);

        if (response && !response.error) {
          // Map API response to form data
          setAssessmentData({
            title: response.title || "",
            description: response.description || "",
            skillId: response.skillId || "",
            type: response.type || "quiz",
            difficultyLevel: response.difficultyLevel || "beginner",
            maxScore: response.maxScore || 100,
            passingScore: response.passingScore || 70,
            timeLimit: response.timeLimit
              ? Math.round(response.timeLimit / 60)
              : 30,
            maxAttempts: response.maxAttempts || 3,
            questions:
              response.questions?.map((q) => ({
                id: q.id,
                questionText: q.questionText,
                questionType: q.questionType || "multiple_choice",
                points: q.points || 10,
                options:
                  q.options?.map((opt) => ({
                    text: opt.option_text,
                    isCorrect: opt.is_correct,
                  })) || [],
              })) || [],
          });
        } else {
          throw new Error(response?.error || "Assessment not found");
        }
      } catch (err) {
        console.error("Error loading assessment:", err);
        setError(`Failed to load assessment: ${err.message}`);
      } finally {
        setLoadingAssessment(false);
      }
    };

    loadAssessmentForEdit();
  }, [isEditMode, editAssessmentId]);

  const assessmentTypes = [
    {
      value: "quiz",
      label: "Quiz",
      icon: BookOpen,
      description: "Multiple choice questions with instant feedback",
    },
    {
      value: "assignment",
      label: "Assignment",
      icon: FileText,
      description: "Project-based assessment with file submissions",
    },
    {
      value: "project",
      label: "Project",
      icon: Code,
      description: "Comprehensive coding or design project",
    },
  ];

  const difficultyLevels = [
    {
      value: "beginner",
      label: "Beginner",
      color: "#10b981",
      description: "Basic concepts and fundamentals",
    },
    {
      value: "intermediate",
      label: "Intermediate",
      color: "#f59e0b",
      description: "Applied knowledge and problem-solving",
    },
    {
      value: "advanced",
      label: "Advanced",
      color: "#ef4444",
      description: "Expert-level concepts and complex scenarios",
    },
  ];

  const questionTypes = [
    {
      value: "multiple_choice",
      label: "Multiple Choice",
      description: "Single correct answer from multiple options",
    },
    {
      value: "multiple_select",
      label: "Multiple Select",
      description: "Multiple correct answers from options",
    },
    {
      value: "true_false",
      label: "True/False",
      description: "Simple true or false question",
    },
    {
      value: "short_answer",
      label: "Short Answer",
      description: "Brief text response",
    },
    {
      value: "essay",
      label: "Essay",
      description: "Long-form written response",
    },
  ];

  const steps = [
    { id: 1, title: "Basic Info", description: "Assessment details" },
    { id: 2, title: "Questions", description: "Add questions" },
    { id: 3, title: "Settings", description: "Configure options" },
    { id: 4, title: "Review", description: "Final review" },
  ];

  // Validation functions
  const validateStep = (step) => {
    const errors = {};

    if (step === 1) {
      if (!assessmentData.title.trim()) {
        errors.title = "Assessment title is required";
      }
      if (!assessmentData.skillId) {
        errors.skillId = "Please select a skill";
      }
      if (
        assessmentData.passingScore < 0 ||
        assessmentData.passingScore > 100
      ) {
        errors.passingScore = "Passing score must be between 0 and 100";
      }
    }

    if (step === 2) {
      if (assessmentData.questions.length === 0) {
        errors.questions = "At least one question is required";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculateProgress = () => {
    let progress = 0;
    if (assessmentData.title && assessmentData.skillId) progress += 25;
    if (assessmentData.questions.length > 0) progress += 50;
    if (assessmentData.questions.length >= 3) progress += 25;
    return Math.min(progress, 100);
  };

  const getTotalPoints = () => {
    return assessmentData.questions.reduce(
      (total, q) => total + (q.points || 0),
      0,
    );
  };

  const getEstimatedTime = () => {
    const baseTime = assessmentData.questions.length * 2; // 2 minutes per question
    return Math.max(baseTime, 5);
  };

  // Auto-update maxScore when questions change
  useEffect(() => {
    const totalPoints = assessmentData.questions.reduce(
      (total, q) => total + (q.points || 0),
      0,
    );
    if (totalPoints > 0 && totalPoints !== assessmentData.maxScore) {
      setAssessmentData((prev) => ({
        ...prev,
        maxScore: totalPoints,
      }));
    }
  }, [assessmentData.questions, assessmentData.maxScore]);

  const handleInputChange = (field, value) => {
    setAssessmentData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleQuestionChange = (field, value) => {
    setCurrentQuestion((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...currentQuestion.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setCurrentQuestion((prev) => ({
      ...prev,
      options: newOptions,
    }));
  };

  const addOption = () => {
    setCurrentQuestion((prev) => ({
      ...prev,
      options: [...prev.options, { text: "", isCorrect: false }],
    }));
  };

  const removeOption = (index) => {
    if (currentQuestion.options.length > 2) {
      const newOptions = currentQuestion.options.filter((_, i) => i !== index);
      setCurrentQuestion((prev) => ({
        ...prev,
        options: newOptions,
      }));
    }
  };

  const toggleCorrectAnswer = (index) => {
    const newOptions = [...currentQuestion.options];

    if (currentQuestion.questionType === "multiple_choice") {
      // For multiple choice, only one correct answer
      newOptions.forEach((option, i) => {
        option.isCorrect = i === index;
      });
    } else {
      // For other types, toggle the specific option
      newOptions[index].isCorrect = !newOptions[index].isCorrect;
    }

    setCurrentQuestion((prev) => ({
      ...prev,
      options: newOptions,
    }));
  };

  // Drag and drop functions
  const handleDragStart = (e, index) => {
    setDraggedQuestion(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedQuestion === null || draggedQuestion === dropIndex) return;

    const newQuestions = [...assessmentData.questions];
    const draggedItem = newQuestions[draggedQuestion];

    // Remove dragged item
    newQuestions.splice(draggedQuestion, 1);

    // Insert at new position
    const insertIndex = draggedQuestion < dropIndex ? dropIndex - 1 : dropIndex;
    newQuestions.splice(insertIndex, 0, draggedItem);

    setAssessmentData((prev) => ({
      ...prev,
      questions: newQuestions,
    }));

    setDraggedQuestion(null);
  };

  const duplicateQuestion = (index) => {
    const questionToDuplicate = { ...assessmentData.questions[index] };
    questionToDuplicate.id = Date.now();
    questionToDuplicate.questionText = `${questionToDuplicate.questionText} (Copy)`;

    const newQuestions = [...assessmentData.questions];
    newQuestions.splice(index + 1, 0, questionToDuplicate);

    setAssessmentData((prev) => ({
      ...prev,
      questions: newQuestions,
    }));
  };

  const saveQuestion = () => {
    if (
      !currentQuestion.questionText.trim() ||
      currentQuestion.options.some((opt) => !opt.text.trim()) ||
      !currentQuestion.options.some((opt) => opt.isCorrect)
    ) {
      alert("Please fill in all fields and select at least one correct answer");
      return;
    }

    const questionToSave = {
      ...currentQuestion,
      id: editingQuestionIndex !== null ? editingQuestionIndex : Date.now(),
    };

    if (editingQuestionIndex !== null) {
      const newQuestions = [...assessmentData.questions];
      newQuestions[editingQuestionIndex] = questionToSave;
      setAssessmentData((prev) => ({
        ...prev,
        questions: newQuestions,
      }));
      setEditingQuestionIndex(null);
    } else {
      setAssessmentData((prev) => ({
        ...prev,
        questions: [...prev.questions, questionToSave],
      }));
    }

    resetCurrentQuestion();
    setIsAddingQuestion(false);
  };

  const resetCurrentQuestion = () => {
    setCurrentQuestion({
      id: null,
      questionText: "",
      questionType: "multiple_choice",
      points: 10,
      options: [
        { text: "", isCorrect: false },
        { text: "", isCorrect: false },
      ],
    });
  };

  const editQuestion = (index) => {
    setCurrentQuestion(assessmentData.questions[index]);
    setEditingQuestionIndex(index);
    setIsAddingQuestion(true);
  };

  const deleteQuestion = (index) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      const newQuestions = assessmentData.questions.filter(
        (_, i) => i !== index,
      );
      setAssessmentData((prev) => ({
        ...prev,
        questions: newQuestions,
      }));
    }
  };

  const cancelQuestion = () => {
    resetCurrentQuestion();
    setIsAddingQuestion(false);
    setEditingQuestionIndex(null);
  };

  const saveAssessment = async () => {
    const totalPoints = getTotalPoints();

    if (
      !assessmentData.title ||
      !assessmentData.skillId ||
      assessmentData.questions.length === 0
    ) {
      alert("Please fill in all required fields and add at least one question");
      return;
    }

    if (totalPoints === 0) {
      alert("Please ensure your questions have point values assigned");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prepare data to send
      let dataToSend;

      if (isEditMode) {
        // For updates: Don't send questions array to avoid double-counting
        // Backend will calculate maxScore from existing questions in database
        dataToSend = {
          title: assessmentData.title,
          description: assessmentData.description,
          skillId: assessmentData.skillId,
          type: assessmentData.type,
          difficultyLevel: assessmentData.difficultyLevel,
          passingScore: assessmentData.passingScore,
          timeLimit: assessmentData.timeLimit, // Send in minutes, backend will convert to seconds
          maxAttempts: assessmentData.maxAttempts,
          // Don't send maxScore - let backend calculate from existing questions
          // Don't send questions - they're already in the database
        };
      } else {
        // For creates: Send everything including questions
        dataToSend = {
          ...assessmentData,
          maxScore: totalPoints,
          timeLimit: assessmentData.timeLimit, // Send in minutes, backend will convert to seconds
        };
      }

      // Debug: Log the data being sent
      console.log("📤 Sending assessment data:", {
        mode: isEditMode ? "UPDATE" : "CREATE",
        title: dataToSend.title,
        maxScore: dataToSend.maxScore,
        totalQuestionPoints: totalPoints,
        questionCount: assessmentData.questions.length,
        hasQuestions: !!dataToSend.questions,
      });

      let response;

      if (isEditMode) {
        // Update existing assessment
        response = await apiService.assessments.updateAssessment(
          editAssessmentId,
          dataToSend,
        );
      } else {
        // Create new assessment
        response = await apiService.assessments.createAssessment(dataToSend);
      }

      if (response && !response.error) {
        if (isEditMode) {
          // For updates, the response might just be the updated assessment
          setSubmittedAssessmentId(editAssessmentId);
        } else {
          // For creates, get the ID from the response
          setSubmittedAssessmentId(response.assessment?.id || response.id);
        }
        setIsSubmitted(true);
      } else {
        throw new Error(
          response?.error ||
            `Failed to ${isEditMode ? "update" : "create"} assessment`,
        );
      }
    } catch (err) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} assessment:`,
        err,
      );
      setError(
        err.message ||
          `Failed to ${
            isEditMode ? "update" : "create"
          } assessment. Please try again.`,
      );
    } finally {
      setLoading(false);
    }
  };

  const viewAssessmentDetails = () => {
    navigate(`/admin/assessment-details/${submittedAssessmentId}`);
  };

  const createAnotherAssessment = () => {
    setIsSubmitted(false);
    setSubmittedAssessmentId(null);
    setAssessmentData({
      title: "",
      description: "",
      skillId: "",
      type: "quiz",
      difficultyLevel: "beginner",
      maxScore: 0, // Will be auto-calculated when questions are added
      passingScore: 70,
      timeLimit: 30,
      maxAttempts: 3,
      questions: [],
    });
    resetCurrentQuestion();
    setError(null);
  };

  if (skillsLoading || loadingAssessment) {
    return (
      <div className="create-assessment-page">
        <div className="create-assessment-container">
          <div className="loading-state">
            <Loader size={48} className="spinner" />
            <h3>
              {loadingAssessment
                ? "Loading assessment..."
                : "Loading skills..."}
            </h3>
            <p>Please wait while we fetch the required data</p>
          </div>
        </div>
      </div>
    );
  }

  // Show success screen after submission
  if (isSubmitted) {
    return (
      <div className="create-assessment-page">
        <div className="create-assessment-container">
          <div className="success-screen">
            <div className="success-content">
              <div className="success-icon">
                <CheckCircle size={64} />
              </div>
              <h1>
                {isEditMode
                  ? "Assessment Updated Successfully!"
                  : "Assessment Created Successfully!"}
              </h1>
              <p>
                Your assessment "{assessmentData.title}" has been{" "}
                {isEditMode ? "updated" : "created"} and saved.
              </p>

              <div className="assessment-summary">
                <div className="summary-item">
                  <span className="label">Questions:</span>
                  <span className="value">
                    {assessmentData.questions.length}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="label">Skill:</span>
                  <span className="value">
                    {skills.find((s) => s.id === assessmentData.skillId)
                      ?.name || "Unknown"}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="label">Type:</span>
                  <span className="value">{assessmentData.type}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Difficulty:</span>
                  <span className="value">
                    {assessmentData.difficultyLevel}
                  </span>
                </div>
              </div>

              <div className="success-actions">
                <button
                  className="btn btn-primary"
                  onClick={viewAssessmentDetails}
                >
                  <Eye size={16} />
                  View Assessment Details
                </button>
                <button
                  className="btn btn-outline"
                  onClick={createAnotherAssessment}
                >
                  <Plus size={16} />
                  Create Another Assessment
                </button>
                <Link to="/admin/assessments" className="btn btn-outline">
                  <ArrowLeft size={16} />
                  Back to Assessments
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-assessment-page">
      <div className="create-assessment-container">
        {/* Header */}
        <div className="page-header">
          <Link to="/assessments" className="back-button">
            <ArrowLeft size={20} />
            Back to Assessments
          </Link>
          <div className="header-content">
            <h1>{isEditMode ? "Edit Assessment" : "Create New Assessment"}</h1>
            <div className="progress-info">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${calculateProgress()}%` }}
                />
              </div>
              <span className="progress-text">
                {calculateProgress()}% Complete
              </span>
            </div>
          </div>
        </div>

        {/* Step Navigation */}
        <div className="step-navigation">
          {steps.map((step) => (
            <div
              key={step.id}
              className={`step ${currentStep === step.id ? "active" : ""} ${
                currentStep > step.id ? "completed" : ""
              }`}
              onClick={() => {
                if (currentStep > step.id || validateStep(currentStep)) {
                  setCurrentStep(step.id);
                }
              }}
            >
              <div className="step-number">
                {currentStep > step.id ? <CheckCircle size={20} /> : step.id}
              </div>
              <div className="step-info">
                <div className="step-title">{step.title}</div>
                <div className="step-description">{step.description}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="assessment-form">
          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="form-step">
              <div className="step-header">
                <h2>Basic Information</h2>
                <p>Set up the fundamental details of your assessment</p>
              </div>

              <div className="form-grid">
                <div className="form-group full-width">
                  <label htmlFor="title">Assessment Title *</label>
                  <input
                    type="text"
                    id="title"
                    value={assessmentData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    placeholder="Enter a clear, descriptive title"
                    className={`form-input ${
                      validationErrors.title ? "error" : ""
                    }`}
                  />
                  {validationErrors.title && (
                    <span className="error-text">{validationErrors.title}</span>
                  )}
                </div>

                <div className="form-group full-width">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={assessmentData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Describe what this assessment covers and its objectives"
                    className="form-textarea"
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="skillId">Skill Category *</label>
                  <select
                    id="skillId"
                    value={assessmentData.skillId}
                    onChange={(e) =>
                      handleInputChange("skillId", e.target.value)
                    }
                    className={`form-select ${
                      validationErrors.skillId ? "error" : ""
                    }`}
                  >
                    <option value="">Select a skill</option>
                    {Object.entries(skillsByCategory).map(
                      ([category, categorySkills]) => (
                        <optgroup key={category} label={`📁 ${category}`}>
                          {categorySkills.map((skill) => (
                            <option key={skill.id} value={skill.id}>
                              {skill.name}
                            </option>
                          ))}
                        </optgroup>
                      ),
                    )}
                  </select>
                  {validationErrors.skillId && (
                    <span className="error-text">
                      {validationErrors.skillId}
                    </span>
                  )}
                  {assessmentData.skillId && (
                    <span className="form-hint">
                      Category:{" "}
                      {skills.find((s) => s.id === assessmentData.skillId)
                        ?.category || "Other"}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>Difficulty Level</label>
                  <div className="difficulty-options">
                    {difficultyLevels.map((level) => (
                      <div
                        key={level.value}
                        className={`difficulty-card ${
                          assessmentData.difficultyLevel === level.value
                            ? "selected"
                            : ""
                        }`}
                        onClick={() =>
                          handleInputChange("difficultyLevel", level.value)
                        }
                        style={{ "--accent-color": level.color }}
                      >
                        <div className="difficulty-indicator" />
                        <div className="difficulty-content">
                          <div className="difficulty-label">{level.label}</div>
                          <div className="difficulty-description">
                            {level.description}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="assessment-type-section">
                <label>Assessment Type</label>
                <div className="type-grid">
                  {assessmentTypes.map((type) => {
                    const IconComponent = type.icon;
                    return (
                      <div
                        key={type.value}
                        className={`type-card ${
                          assessmentData.type === type.value ? "selected" : ""
                        }`}
                        onClick={() => handleInputChange("type", type.value)}
                      >
                        <IconComponent size={32} />
                        <div className="type-content">
                          <h3>{type.label}</h3>
                          <p>{type.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Questions */}
          {currentStep === 2 && (
            <div className="form-step questions-step">
              <div className="questions-step-header">
                <div className="step-title-section">
                  <h2>Build Your Questions</h2>
                  <p>Create engaging questions to assess your learners</p>
                </div>

                {/* Quick Stats */}
                <div className="questions-quick-stats">
                  <div className="quick-stat">
                    <div className="stat-number">
                      {assessmentData.questions.length}
                    </div>
                    <div className="stat-label">Questions</div>
                  </div>
                  <div className="quick-stat">
                    <div className="stat-number">{getTotalPoints()}</div>
                    <div className="stat-label">Total Points</div>
                  </div>
                  {/* <div className="quick-stat">
                    <div className="stat-number">~{getEstimatedTime()}</div>
                    <div className="stat-label">Minutes</div>
                  </div> */}
                </div>
              </div>

              {validationErrors.questions && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  <span>{validationErrors.questions}</span>
                </div>
              )}

              {/* Empty State */}
              {assessmentData.questions.length === 0 && !isAddingQuestion && (
                <div className="questions-empty-state">
                  <div className="empty-state-icon">
                    <HelpCircle size={64} />
                  </div>
                  <h3>No questions yet</h3>
                  <p>
                    Start building your assessment by adding your first question
                  </p>
                  <button
                    className="btn btn-primary btn-large"
                    onClick={() => setIsAddingQuestion(true)}
                  >
                    <Plus size={20} />
                    Create First Question
                  </button>
                </div>
              )}

              {/* Questions List */}
              {assessmentData.questions.length > 0 && !isAddingQuestion && (
                <div className="questions-container">
                  <div className="questions-toolbar">
                    <div className="toolbar-left">
                      <div className="questions-count">
                        {assessmentData.questions.length} question
                        {assessmentData.questions.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div className="toolbar-right">
                      <button
                        className="btn btn-primary"
                        onClick={() => setIsAddingQuestion(true)}
                      >
                        <Plus size={18} />
                        Add Question
                      </button>
                    </div>
                  </div>

                  <div className="questions-grid">
                    {assessmentData.questions.map((question, index) => (
                      <div
                        key={question.id}
                        className={`modern-question-card ${
                          draggedQuestion === index ? "dragging" : ""
                        }`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                      >
                        {/* Question Header */}
                        <div className="modern-question-header">
                          <div className="question-number-badge">
                            <span className="question-number">
                              Q{index + 1}
                            </span>
                          </div>

                          <div className="question-type-indicator">
                            <span
                              className={`type-badge type-${question.questionType}`}
                            >
                              {
                                questionTypes.find(
                                  (t) => t.value === question.questionType,
                                )?.label
                              }
                            </span>
                          </div>

                          <div className="question-points">
                            <Award size={14} />
                            <span>{question.points} pts</span>
                          </div>

                          <div className="question-actions-dropdown">
                            <button className="action-menu-trigger">
                              <Settings size={16} />
                            </button>
                            <div className="action-menu">
                              <button
                                className="action-item"
                                onClick={() => editQuestion(index)}
                              >
                                <Edit3 size={14} />
                                Edit
                              </button>
                              <button
                                className="action-item"
                                onClick={() => duplicateQuestion(index)}
                              >
                                <Copy size={14} />
                                Duplicate
                              </button>
                              <button
                                className="action-item delete"
                                onClick={() => deleteQuestion(index)}
                              >
                                <Trash2 size={14} />
                                Delete
                              </button>
                            </div>
                          </div>

                          <div className="drag-handle">
                            <GripVertical size={16} />
                          </div>
                        </div>

                        {/* Question Content */}
                        <div className="modern-question-content">
                          <div className="question-text">
                            {question.questionText}
                          </div>

                          {/* Options Preview */}
                          {question.options && question.options.length > 0 && (
                            <div className="options-preview">
                              {question.options.map((option, optIndex) => (
                                <div
                                  key={optIndex}
                                  className={`modern-option-preview ${
                                    option.isCorrect ? "correct" : ""
                                  }`}
                                >
                                  <div className="option-indicator">
                                    {question.questionType ===
                                    "multiple_choice" ? (
                                      <div
                                        className={`radio-indicator ${
                                          option.isCorrect ? "selected" : ""
                                        }`}
                                      />
                                    ) : (
                                      <div
                                        className={`checkbox-indicator ${
                                          option.isCorrect ? "selected" : ""
                                        }`}
                                      >
                                        {option.isCorrect && (
                                          <CheckSquare size={12} />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <span className="option-text">
                                    {option.text}
                                  </span>
                                  {option.isCorrect && (
                                    <div className="correct-badge">
                                      <CheckCircle size={14} />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Question Button at Bottom */}
                  <div className="add-question-bottom">
                    <button
                      className="btn btn-outline btn-large add-question-btn"
                      onClick={() => setIsAddingQuestion(true)}
                    >
                      <Plus size={20} />
                      Add Another Question
                    </button>
                  </div>
                </div>
              )}

              {/* Modern Question Form */}
              {isAddingQuestion && (
                <div className="modern-question-form">
                  <div className="question-form-header">
                    <h3>
                      {editingQuestionIndex !== null
                        ? "Edit Question"
                        : "Create New Question"}
                    </h3>
                    <button className="close-form-btn" onClick={cancelQuestion}>
                      ×
                    </button>
                  </div>

                  <div className="question-form-content">
                    {/* Question Text */}
                    <div className="form-section">
                      <label className="form-label">Question Text *</label>
                      <div className="question-input-container">
                        <textarea
                          value={currentQuestion.questionText}
                          onChange={(e) =>
                            handleQuestionChange("questionText", e.target.value)
                          }
                          placeholder="What would you like to ask your learners?"
                          className="modern-question-textarea"
                          rows="3"
                        />
                        <div className="character-count">
                          {currentQuestion.questionText.length}/500
                        </div>
                      </div>
                    </div>

                    {/* Question Settings */}
                    <div className="question-settings-row">
                      <div className="setting-group">
                        <label className="form-label">Question Type</label>
                        <div className="question-type-selector">
                          {questionTypes.map((type) => (
                            <button
                              key={type.value}
                              type="button"
                              className={`type-option ${
                                currentQuestion.questionType === type.value
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() =>
                                handleQuestionChange("questionType", type.value)
                              }
                            >
                              <span className="type-name">{type.label}</span>
                              <span className="type-description">
                                {type.description}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="setting-group points-setting">
                        <label className="form-label">Points</label>
                        <div className="points-input-container">
                          <input
                            type="number"
                            value={currentQuestion.points}
                            onChange={(e) =>
                              handleQuestionChange(
                                "points",
                                parseInt(e.target.value) || 1,
                              )
                            }
                            min="1"
                            max="100"
                            className="points-input"
                          />
                          <span className="points-label">pts</span>
                        </div>
                      </div>
                    </div>

                    {/* Answer Options */}
                    {(currentQuestion.questionType === "multiple_choice" ||
                      currentQuestion.questionType === "multiple_select") && (
                      <div className="form-section">
                        <div className="options-header">
                          <label className="form-label">Answer Options *</label>
                          <div className="options-hint">
                            {currentQuestion.questionType === "multiple_choice"
                              ? "Select one correct answer"
                              : "Select all correct answers"}
                          </div>
                        </div>

                        <div className="modern-options-list">
                          {currentQuestion.options.map((option, index) => (
                            <div key={index} className="modern-option-input">
                              <div className="option-number">
                                {String.fromCharCode(65 + index)}
                              </div>

                              <div className="option-input-wrapper">
                                <input
                                  type="text"
                                  value={option.text}
                                  onChange={(e) =>
                                    handleOptionChange(
                                      index,
                                      "text",
                                      e.target.value,
                                    )
                                  }
                                  placeholder={`Enter option ${String.fromCharCode(
                                    65 + index,
                                  )}`}
                                  className="modern-option-text-input"
                                />
                              </div>

                              <div className="option-controls">
                                <button
                                  type="button"
                                  className={`correct-toggle ${
                                    option.isCorrect ? "active" : ""
                                  }`}
                                  onClick={() => toggleCorrectAnswer(index)}
                                  title="Mark as correct"
                                >
                                  {currentQuestion.questionType ===
                                  "multiple_choice" ? (
                                    <div
                                      className={`radio-control ${
                                        option.isCorrect ? "selected" : ""
                                      }`}
                                    />
                                  ) : (
                                    <CheckSquare size={16} />
                                  )}
                                </button>

                                {currentQuestion.options.length > 2 && (
                                  <button
                                    type="button"
                                    className="remove-option-btn"
                                    onClick={() => removeOption(index)}
                                    title="Remove option"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        <button
                          type="button"
                          className="add-option-btn"
                          onClick={addOption}
                          disabled={currentQuestion.options.length >= 6}
                        >
                          <Plus size={16} />
                          Add Option
                        </button>
                      </div>
                    )}

                    {/* True/False Options */}
                    {currentQuestion.questionType === "true_false" && (
                      <div className="form-section">
                        <label className="form-label">Correct Answer *</label>
                        <div className="true-false-options">
                          <button
                            type="button"
                            className={`tf-option ${
                              currentQuestion.options[0]?.isCorrect
                                ? "selected"
                                : ""
                            }`}
                            onClick={() => {
                              const newOptions = [
                                { text: "True", isCorrect: true },
                                { text: "False", isCorrect: false },
                              ];
                              setCurrentQuestion((prev) => ({
                                ...prev,
                                options: newOptions,
                              }));
                            }}
                          >
                            <CheckCircle size={16} />
                            True
                          </button>
                          <button
                            type="button"
                            className={`tf-option ${
                              currentQuestion.options[1]?.isCorrect
                                ? "selected"
                                : ""
                            }`}
                            onClick={() => {
                              const newOptions = [
                                { text: "True", isCorrect: false },
                                { text: "False", isCorrect: true },
                              ];
                              setCurrentQuestion((prev) => ({
                                ...prev,
                                options: newOptions,
                              }));
                            }}
                          >
                            <CheckCircle size={16} />
                            False
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Form Actions */}
                    <div className="question-form-actions">
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={cancelQuestion}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={saveQuestion}
                      >
                        <Save size={16} />
                        {editingQuestionIndex !== null
                          ? "Update Question"
                          : "Save Question"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Settings */}
          {currentStep === 3 && (
            <div className="form-step">
              <div className="step-header">
                <h2>Assessment Settings</h2>
                <p>Configure scoring, timing, and attempt limits</p>
              </div>

              <div className="settings-grid">
                <div className="settings-card">
                  <div className="settings-header">
                    <Award size={24} />
                    <h3>Scoring</h3>
                  </div>
                  <div className="form-group">
                    <label htmlFor="maxScore">Maximum Score</label>
                    <div className="auto-calculated-field">
                      <input
                        type="number"
                        id="maxScore"
                        value={assessmentData.maxScore}
                        readOnly
                        className="form-input auto-calculated"
                      />
                      <div className="auto-calculated-info">
                        <small className="form-hint">
                          {getTotalPoints() > 0
                            ? `Auto-calculated from question points (${getTotalPoints()} total)`
                            : "Add questions to calculate maximum score"}
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="passingScore">Passing Score (%)</label>
                    <input
                      type="number"
                      id="passingScore"
                      value={assessmentData.passingScore}
                      onChange={(e) =>
                        handleInputChange(
                          "passingScore",
                          parseInt(e.target.value),
                        )
                      }
                      min="0"
                      max="100"
                      className={`form-input ${
                        validationErrors.passingScore ? "error" : ""
                      }`}
                    />
                    {validationErrors.passingScore && (
                      <span className="error-text">
                        {validationErrors.passingScore}
                      </span>
                    )}
                    <small className="form-hint">
                      {assessmentData.passingScore}% of {getTotalPoints()}{" "}
                      points ={" "}
                      {Math.round(
                        (assessmentData.passingScore / 100) * getTotalPoints(),
                      )}{" "}
                      points needed to pass
                    </small>
                  </div>
                </div>

                <div className="settings-card">
                  <div className="settings-header">
                    <Clock size={24} />
                    <h3>Timing</h3>
                  </div>
                  <div className="form-group">
                    <label htmlFor="timeLimit">Time Limit (minutes)</label>
                    <input
                      type="number"
                      id="timeLimit"
                      value={assessmentData.timeLimit}
                      onChange={(e) =>
                        handleInputChange("timeLimit", parseInt(e.target.value))
                      }
                      min="1"
                      className="form-input"
                    />
                    <small className="form-hint">
                      Recommended: {getEstimatedTime()} minutes based on
                      question count
                    </small>
                  </div>
                </div>

                <div className="settings-card">
                  <div className="settings-header">
                    <Users size={24} />
                    <h3>Attempts</h3>
                  </div>
                  <div className="form-group">
                    <label htmlFor="maxAttempts">Maximum Attempts</label>
                    <input
                      type="number"
                      id="maxAttempts"
                      value={assessmentData.maxAttempts}
                      onChange={(e) =>
                        handleInputChange(
                          "maxAttempts",
                          parseInt(e.target.value),
                        )
                      }
                      min="0"
                      className="form-input"
                    />
                    <small className="form-hint">
                      Set to 0 for unlimited attempts
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="form-step">
              <div className="step-header">
                <h2>Review & Publish</h2>
                <p>Review your assessment before publishing</p>
              </div>

              <div className="review-container">
                <div className="review-summary">
                  <h3>Assessment Summary</h3>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span className="label">Title:</span>
                      <span className="value">{assessmentData.title}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Skill:</span>
                      <span className="value">
                        {skills.find((s) => s.id === assessmentData.skillId)
                          ?.name || "Unknown"}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Type:</span>
                      <span className="value">{assessmentData.type}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Difficulty:</span>
                      <span className="value">
                        {assessmentData.difficultyLevel}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Questions:</span>
                      <span className="value">
                        {assessmentData.questions.length}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Total Points:</span>
                      <span className="value">{getTotalPoints()}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Time Limit:</span>
                      <span className="value">
                        {assessmentData.timeLimit} minutes
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Passing Score:</span>
                      <span className="value">
                        {assessmentData.passingScore}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="review-questions">
                  <h3>Questions Preview</h3>
                  <div className="questions-preview">
                    {assessmentData.questions.map((question, index) => (
                      <div key={question.id} className="preview-question">
                        <div className="preview-header">
                          <span className="question-number">Q{index + 1}</span>
                          <span className="points">{question.points} pts</span>
                        </div>
                        <p className="question-text">{question.questionText}</p>
                        <div className="preview-options">
                          {question.options.map((option, optIndex) => (
                            <div
                              key={optIndex}
                              className={`preview-option ${
                                option.isCorrect ? "correct" : ""
                              }`}
                            >
                              <span className="option-letter">
                                {String.fromCharCode(65 + optIndex)}
                              </span>
                              <span>{option.text}</span>
                              {option.isCorrect && <CheckCircle size={16} />}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="step-navigation-buttons">
            {currentStep > 1 && (
              <button
                className="btn btn-outline"
                onClick={() => setCurrentStep(currentStep - 1)}
              >
                <ArrowLeft size={16} />
                Previous
              </button>
            )}

            <div className="nav-spacer" />

            {currentStep < 4 ? (
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (validateStep(currentStep)) {
                    setCurrentStep(currentStep + 1);
                  }
                }}
              >
                Next
                <ArrowLeft size={16} style={{ transform: "rotate(180deg)" }} />
              </button>
            ) : (
              <button
                className="btn btn-primary btn-large"
                onClick={saveAssessment}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader size={16} className="spinner" />
                    {isEditMode ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {isEditMode ? "Update Assessment" : "Publish Assessment"}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAssessment;
